import { useEffect, useState } from 'react';

import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { mutations, queries } from 'RestAPIs/rapini';

import { Dialog, showSnackbar } from 'components/shared';
import { useDebounce } from 'usehooks-ts';
import { getPlanUnitIds } from 'components/utils';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  assignEmploymentEditAppointmentTypeAtom,
  planUnitsAtom
} from '../../atoms';
import { Event } from '../../types';
import EventInfo from '../EventInfo/EventInfo';
import AssignEmploymentGrid from 'components/shared/AssignEmploymentGrid/AssignEmploymentGrid';
import {
  AssignEmploymentToAppointmentRequest,
  MatchCandidateResponse,
  PlanningEntityIdentifier,
  SchedulingActionResultResponse
} from 'generated/index';
import { EditAppointmentType } from 'RestAPIs/activities/types';
import { scrollToAppointment } from '../../utils';

type Props = {
  open: boolean;
  onClose: () => void;
  event?: Event;
  date: string;
  title?: string;
  debounceInMS?: number;
  editAppointmentType?: EditAppointmentType;
};

export default function AssignEmploymentDialog({
  open,
  onClose,
  event,
  date,
  title,
  debounceInMS = 500
}: Props) {
  const { t } = useTranslation('common');
  const selectedPlanUnits = useRecoilValue(planUnitsAtom);
  const [
    assignEmploymentEditAppointmentType,
    setAssignEmploymentEditAppointmentType
  ] = useRecoilState<EditAppointmentType | null | undefined>(
    assignEmploymentEditAppointmentTypeAtom
  );

  const isWorkPlaceAppointment = event?.schedulingLabel === 'WORKPLACE';

  const queryClient = useQueryClient();
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedQuery = useDebounce(searchValue, debounceInMS);
  const [showInactiveEmployments, setShowInactiveEmployments] =
    useState<boolean>(false);

  const {
    data: candidates,
    isFetching: isFetchingCandidates,
    refetch
  } = queries.useFindMatchCandidatesForAppointments(
    event?.id ?? '',
    dayjs(date).startOf('day').format('YYYY-MM-DD'),
    //@ts-expect-error TODO: BE should accept array of ids
    searchValue.length > 0
      ? undefined
      : getPlanUnitIds(selectedPlanUnits).join(','),
    searchValue.length > 0 ? searchValue : undefined,
    !showInactiveEmployments,
    {
      enabled: false
    }
  );

  const { mutate: assignEmployment, isLoading } =
    mutations.useAssignEmploymentToAppointment();

  async function onAssign(candidate: MatchCandidateResponse) {
    const payload: AssignEmploymentToAppointmentRequest = {
      correlationToken: '',
      appointmentOccurrence: {
        entityId: event?.id,
        originalOccurrenceStart: event?.start,
        schedulingLabel: event?.schedulingLabel
      },
      planningEntityIdentifier: {
        planningEntityType: 'EMPLOYMENT',
        entityId: candidate.employmentEntityId,
        contractNumber: candidate.employmentNumber,
        number: candidate.employeeNumber
      } as PlanningEntityIdentifier,
      editAppointmentType: assignEmploymentEditAppointmentType || 'ONLY_THIS',
      suppressWarnings: true
    };

    if (!event || !candidate) return;

    assignEmployment(payload, {
      onSuccess: (response: SchedulingActionResultResponse) => {
        handleClose();
        if (
          !response.planningEntityIdentifiers ||
          response.planningEntityIdentifiers.length === 0
        ) {
          queryClient.invalidateQueries('planning');
        }
        scrollToAppointment(event.id, event.start, 2000);
      },
      onError: () => {
        const message = t('somethingWentWrong');
        showSnackbar({
          severity: 'error',
          message: message
        });
      }
    });
  }

  const handleClose = () => {
    setAssignEmploymentEditAppointmentType(null);
    onClose?.();
  };

  useEffect(() => {
    if (open) {
      setSearchValue('');
      refetch();
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      refetch();
    }
  }, [debouncedQuery, showInactiveEmployments]);

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      handleClose={handleClose}
      title={title ? `Medewerker koppelen: ${title}` : 'Medewerker koppelen'}
    >
      {event && (
        <EventInfo
          date={event.date}
          activity={event.activity?.name}
          start={event.start}
          end={event.end}
          workPlace={event.workPlace?.name}
          qualifications={(event.activity?.requiredQualifications || [])
            .map((q) => q.name)
            .join(', ')}
        />
      )}
      <AssignEmploymentGrid
        candidates={candidates}
        isLoading={isLoading || isFetchingCandidates}
        onAssign={onAssign}
        onSearch={setSearchValue}
        onShowInactiveEmployments={setShowInactiveEmployments}
        hideContractMatch
        hideAppointments={!isWorkPlaceAppointment}
        hideDuties={!isWorkPlaceAppointment}
        hideQualifications={!isWorkPlaceAppointment}
        hideFunctionMatch
        hideResponse
        showInactiveEmployments={showInactiveEmployments}
      />
    </Dialog>
  );
}
